// src/store/slices/userSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';

// Types
interface UserIntegration {
  integration: {
    key: string;
    _id: string;
    [key: string]: any;
  };
  isActive: boolean;
  details: Array<{
    phoneNumberId: string;
    [key: string]: any;
  }>;
  [key: string]: any;
}

export interface UserState {
  currentUser: {
    _id: string;
    firstName: string;
    lastName?: string;
    email?: string;
    [key: string]: any;
  } | null;
  userIntegrations: UserIntegration[];
  whatsappIntegration: UserIntegration | null;
  isWhatsappIntegrated: boolean;
  teamMembers: Array<{
    value: string;
    label: string;
  }>;
}

// Initial state
const initialState: UserState = {
  currentUser: null,
  userIntegrations: [],
  whatsappIntegration: null,
  isWhatsappIntegrated: false,
  teamMembers: [],
};

// Helper function to extract team members
const extractTeamMembers = (organizationEmployees: any[]) => {
  return organizationEmployees?.map((item: any) => {
    return {
      value: item?._id,
      label: `${item?.firstName || ''} ${item?.lastName || ''}`.trim(),
    };
  }) || [];
};

// Create the slice
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setCurrentUser: (state, action: PayloadAction<any>) => {
      state.currentUser = action.payload;
      
      // Extract user integrations
      state.userIntegrations = action.payload?.userIntegrations || [];
      
      // Find WhatsApp integration
      state.whatsappIntegration = state.userIntegrations.find(
        item => item?.integration?.key === "whatsapp_business" && item?.isActive
      ) || null;
      
      state.isWhatsappIntegrated = !!state.whatsappIntegration;
      
      // Extract team members
      state.teamMembers = extractTeamMembers(action.payload?.organizationEmployee || []);
    },
    
    updateUserIntegrations: (state, action: PayloadAction<UserIntegration[]>) => {
      state.userIntegrations = action.payload;
      
      // Find WhatsApp integration
      state.whatsappIntegration = state.userIntegrations.find(
        item => item?.integration?.key === "whatsapp_business" && item?.isActive
      ) || null;
      
      state.isWhatsappIntegrated = !!state.whatsappIntegration;
    },
    
    setTeamMembers: (state, action: PayloadAction<any[]>) => {
      state.teamMembers = extractTeamMembers(action.payload);
    }
  }
});

// Export actions
export const {
  setCurrentUser,
  updateUserIntegrations,
  setTeamMembers
} = userSlice.actions;

// Export selectors
export const selectCurrentUser = (state: RootState) => state.whatsapp.user.currentUser;
export const selectUserIntegrations = (state: RootState) => state.whatsapp.user.userIntegrations;
export const selectWhatsappIntegration = (state: RootState) => state.whatsapp.user.whatsappIntegration;
export const selectIsWhatsappIntegrated = (state: RootState) => state.whatsapp.user.isWhatsappIntegrated;
export const selectTeamMembers = (state: RootState) => state.whatsapp.user.teamMembers;
export const selectWhatsappPhoneId = (state: RootState) => 
  state.whatsapp.user.whatsappIntegration?.details?.[0]?.phoneNumberId || '';

// Export reducer
export default userSlice.reducer;